import CustomImage from "./CustomImage"
import type { ImageProps } from "next/image"

// Minimal event shape to extract poster info
type BasicEvent =
    | {
          poster?: string
          mobile_poster?: string
          posters?: string[]
          media?: {
              posters?: {
                  web_poster?: { 
                      original?: string;
                      thumbnails?: {
                          [key: string]: string;
                      }
                  }
                  mobile_poster?: { 
                      original?: string;
                      thumbnails?: {
                          [key: string]: string;
                      }
                  }
                  gallery?: { 
                      original?: string;
                      thumbnails?: {
                          [key: string]: string;
                      }
                  }[]
              }
          }
      }
    | null
    | undefined

// Normalize path to avoid double slashes: remove leading slash if present
const normalizePath = (path: string): string => {
    return path.startsWith("/") ? path.slice(1) : path
}

const isInvalidPosterPath = (path: string): boolean => {
    const normalized = normalizePath(path).toLowerCase()
    return (
        !normalized ||
        normalized === "undefined" ||
        normalized.includes("/undefined/") ||
        normalized.startsWith("undefined/")
    )
}

// Combine base URL and path cleanly: ensure base has no trailing slash, path has no leading slash
const combineUrlAndPath = (base: string, path: string): string => {
    const cleanBase = base.replace(/\/$/, "")
    const cleanPath = normalizePath(path)
    return cleanPath ? `${cleanBase}/${cleanPath}` : cleanBase
}

export const getEventPosterPath = (
    ev: BasicEvent,
    variant: "mobile" | "desktop" = "mobile"
): string => {
    // Prefer direct poster/mobile_poster (from optimized endpoints, which already prioritize smaller thumbnails)
    const directMobile = ev?.mobile_poster || "";
    const directDesktop = ev?.poster || "";
    
    // Helper to get smallest available thumbnail
    const getSmallestThumb = (thumbnails?: Record<string, string>) => {
        if (!thumbnails) return "";
        // Check in order from smallest to largest
        const keysToCheck = [
            "200", "200x200", "thumb_0",
            "400", "400x400", "thumb_1",
            "500", "500x500",
            "800", "800x800",
            "1000", "1000x1000"
        ];
        for (const key of keysToCheck) {
            if (thumbnails[key]) {
                return thumbnails[key];
            }
        }
        return "";
    };
    
    // Fall back to checking nested media for thumbnails, then originals
    const nestedMobile = 
        getSmallestThumb(ev?.media?.posters?.mobile_poster?.thumbnails) || 
        ev?.media?.posters?.mobile_poster?.original || "";
    const nestedDesktop = 
        getSmallestThumb(ev?.media?.posters?.web_poster?.thumbnails) || 
        ev?.media?.posters?.web_poster?.original || "";
        
    const legacySingle = directDesktop || ""
    let legacyArrayFirst = ""
    const posters = ev?.posters
    if (Array.isArray(posters) && posters.length > 0) {
        legacyArrayFirst = posters[0] || ""
    }

    const mobile = directMobile || nestedMobile;
    const desktop = directDesktop || nestedDesktop;

    const preferred =
        variant === "mobile" 
            ? mobile || legacySingle || legacyArrayFirst 
            : desktop || legacySingle || legacyArrayFirst;

    const selected = preferred || "";
    return isInvalidPosterPath(selected) ? "" : selected;
}

export const getEventPosterUrl = (
    ev: BasicEvent,
    variant: "mobile" | "desktop" = "mobile"
): string => {
    const base = (process.env.NEXT_PUBLIC_Event_Img_URL || "").replace(
        /\/$/,
        ""
    )
    const path = getEventPosterPath(ev, variant)
    return path
        ? combineUrlAndPath(base, path)
        : "/svg/v2/pass_ticket_fallback_image_light.png"
}

// Some legacy components expect a path relative to "/images/".
export const getEventPosterRelativeToImages = (
    ev: BasicEvent,
    variant: "mobile" | "desktop" = "mobile"
): string => {
    const path = getEventPosterPath(ev, variant) || ""
    if (!path) return ""
    const normalized = normalizePath(path)
    // If already starts with images/, strip it; otherwise, keep as is
    return normalized.startsWith("images/")
        ? normalized.slice("images/".length)
        : normalized
}

type EventPosterProps = Omit<ImageProps, "src"> & {
    event: BasicEvent
    variant?: "mobile" | "desktop"
}

export default function EventPoster({
    event,
    variant = "mobile",
    alt = "",
    ...props
}: EventPosterProps) {
    const src = getEventPosterUrl(event, variant)
    return <CustomImage src={src} alt={alt} {...props} />
}
